/**
 * Los accesos del equipo en la ficha: cuenta, contrasena y patron.
 *
 * No vienen con la orden. La lista de ordenes dice que HAY accesos cargados
 * (`tiene_accesos`) y nada mas; los tres se piden recien cuando alguien aprieta
 * "Ver accesos", que es lo que pasa una vez por orden, al armar el talon que
 * viaja con el equipo. Asi la contrasena de un cliente no anda por la red cada
 * vez que se refresca el tablero.
 */
import { useState } from "react";
import { Eye, EyeOff, KeyRound } from "lucide-react";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { useAccesosOt } from "@/lib/queries";

import { textoAPatron } from "./logica";
import { PatronDesbloqueo } from "./PatronDesbloqueo";

export function AccesosDelEquipo({ ordenId }: { ordenId: number }): JSX.Element {
  const [visibles, setVisibles] = useState(false);
  const { data, isPending, isError, refetch } = useAccesosOt(ordenId, visibles);

  return (
    <Card variant="muted" padding="compacto" className="space-y-2">
      {/* Eyebrow sin heading: el bloque vive adentro de "El equipo", que ya es el h3. */}
      <TituloSeccion
        nivel="etiqueta"
        as="span"
        icono={KeyRound}
        acciones={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setVisibles((v) => !v)}
            aria-expanded={visibles}
          >
            {visibles ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            {visibles ? "Ocultar" : "Ver accesos"}
          </Button>
        }
      >
        Accesos del equipo
      </TituloSeccion>

      {!visibles ? null : isPending ? (
        <Cargando tamano="inline" texto="Buscando…" />
      ) : isError || !data ? (
        <Aviso
          tono="destructive"
          compacto
          accion={
            <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
              Reintentar
            </Button>
          }
        >
          No se pudieron traer los accesos.
        </Aviso>
      ) : (
        <dl className="space-y-2 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Cuenta</dt>
            <dd className="min-w-0 truncate text-right">{data.cuenta_equipo ?? "—"}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Contraseña</dt>
            <dd className="min-w-0 truncate text-right">
              <Codigo>{data.password_equipo ?? "—"}</Codigo>
            </dd>
          </div>
          {data.patron_equipo ? (
            <div className="space-y-1.5">
              <dt className="text-muted-foreground">Clave del equipo: patrón</dt>
              <dd>
                <PatronDesbloqueo
                  valor={textoAPatron(data.patron_equipo)}
                  onCambiar={() => {}}
                  soloLectura
                />
              </dd>
            </div>
          ) : (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="shrink-0 text-muted-foreground">Clave del equipo</dt>
              <dd className="min-w-0 truncate text-right">
                <Codigo>{data.pin_equipo ?? "—"}</Codigo>
              </dd>
            </div>
          )}
        </dl>
      )}
    </Card>
  );
}
